import React from 'react';
import { EnhancedEventCard } from '@eb/eds-event-card';

import { useUser, useLazyImages } from '../../hooks';
import {
    useSaveEvent,
    useSaveEventToCollection,
    useCreateCollection,
    useEventCollections,
    useShouldSaveFromLoggedOutRedirect,
    useAffCode,
    useFollowingSignalExperiment,
} from './hooks';
import { EventCardProps } from './types';
import {
    trackEventClick,
    trackSaveEvent,
    trackShowCreateCollectionView,
    trackShowAddToCollectionView,
    trackSaveEventToCollection,
    trackCreateCollection,
} from './analytics';

export const EventCard: React.FunctionComponent<EventCardProps> = (props) => {
    //Prop Breakdown
    const {
        eventData: {
            id: eventId,
            organizerFollowerCount,
            savedByYou,
            ...eventData
        },
        isLazyImage,
        currencyFormat,
        gaCategory,
        isAuthenticated,
        user: userFromProps,
        onClick,
        onSave,
        onShowCreateCollectionView,
        onShowAddToCollectionView,
        onSaveEventToCollection,
        onCreateCollection,
        ...otherProps
    } = props;

    //Initialize document event listener for lazy loading images
    const { shouldUseLazyImages } = useLazyImages(isLazyImage);
    const signalData = useFollowingSignalExperiment(organizerFollowerCount);

    //Local state
    const [saved, setSaved] = React.useState(!!savedByYou);

    //Data fetchers
    const user = useUser();
    const {
        eventCollections,
        refetch: fetchEventCollections,
    } = useEventCollections(eventId, user?.collections || [], {
        enabled: false,
    });

    //Action handlers and tracking helpers
    const handleSaveEvent = useSaveEvent({
        setSaved,
        onCompleted: () => {
            onSave?.(saved, eventId);

            gaCategory &&
                isAuthenticated &&
                !onSave &&
                trackSaveEvent(gaCategory, !saved, eventId);
        },
    });

    //Currently does not display success notification
    //However save is successful
    //TODO: Ensure notification fires
    useShouldSaveFromLoggedOutRedirect(
        handleSaveEvent,
        eventId,
        isAuthenticated,
    );

    const handleSaveEventToCollection = useSaveEventToCollection({
        onCompleted: ({
            collectionId,
            isChecked,
        }: {
            collectionId: string;
            isChecked: boolean;
        }) => {
            !onSaveEventToCollection &&
                trackSaveEventToCollection(gaCategory, isChecked, collectionId);
            onSaveEventToCollection?.({
                isChecked,
                collectionId,
                eventId: eventId,
            });
        },
    });

    const handleCreateCollection = useCreateCollection({
        userId: user?.id || '',
        onCompleted: (collectionId: string, collectionName: string) => {
            !onCreateCollection &&
                trackCreateCollection(gaCategory, collectionId);
            onCreateCollection?.(collectionName);
        },
    });

    const handleOnClick = React.useCallback(() => {
        gaCategory &&
            !onClick &&
            trackEventClick(gaCategory, eventId, organizerFollowerCount);
        onClick?.(eventId);
    }, [eventId, gaCategory, onClick, organizerFollowerCount]);

    const handleShowCreateCollection = React.useCallback(() => {
        gaCategory &&
            !onShowCreateCollectionView &&
            trackShowCreateCollectionView(gaCategory);
        onShowCreateCollectionView?.();
    }, [gaCategory, onShowCreateCollectionView]);

    const handleShowAddToCollection = React.useCallback(() => {
        gaCategory &&
            !onShowAddToCollectionView &&
            trackShowAddToCollectionView(gaCategory);

        onShowAddToCollectionView?.();

        user.fetchUserData(userFromProps).then(() => {
            if (!eventCollections) {
                return fetchEventCollections();
            }
        });
    }, [
        gaCategory,
        onShowAddToCollectionView,
        user,
        userFromProps,
        eventCollections,
        fetchEventCollections,
    ]);

    if (eventData.minPrice && currencyFormat) {
        eventData.minPrice.currencyFormat = currencyFormat;
    }

    eventData.url = useAffCode(eventData.url, props.affCode || '');

    return (
        <EnhancedEventCard
            //Let passed in props overwrite
            //the signal info if necessary
            {...signalData}
            {...otherProps}
            {...eventData}
            id={eventId}
            isLazyImage={shouldUseLazyImages && isLazyImage}
            isAuthenticated={isAuthenticated}
            onAddOrRemoveEventToCollection={(data: {
                isChecked: boolean;
                value: string;
            }) =>
                handleSaveEventToCollection({
                    isChecked: data.isChecked,
                    collectionId: data.value,
                    eventId,
                })
            }
            onClick={handleOnClick}
            onCreateCollection={(name: string) =>
                handleCreateCollection({ name, eventId })
            }
            onSaveEvent={() =>
                handleSaveEvent({
                    eventId,
                    isAuthenticated: !!isAuthenticated,
                    savedByYou: saved,
                })
            }
            onShowAddToCollectionView={handleShowAddToCollection}
            onShowCreateCollectionView={handleShowCreateCollection}
            savedByYou={saved}
            userCollections={user.collections || []}
            eventCollections={eventCollections || []}
        />
    );
};
